import { useState, useEffect, type ReactElement } from "react";
import { format } from "date-fns";
import { getInvoiceProfitability, getInvoiceSubject } from "@/lib/invoices/invoice-subject";
import {
  MoreHorizontal,
  Eye,
  FileMinus2,
  Send,
  FileText,
  CheckCircle2,
  Ban,
  CheckCircle,
  Download,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FilterItem } from "@/interfaces";
import MainLayout from "@/components/layouts";
import { PageHeader } from "@/components/layouts/page-header/index.tsx";
import { Link } from "react-router";
import { useInvoices, useUpdateInvoiceStatus, useBulkUpdateInvoiceStatus } from "./invoices.hooks";
import { invoice_statuses } from "@/models/invoice.model.ts";
import { LISTED_INVOICE_STATUSES, type ChangedInvoiceStatus, type InvoiceListFilters, type InvoiceListJoin, type ListedInvoiceStatus } from "@/repositories/invoice-records";
import { useAppDispatch } from "@/lib/store";
import { getInvoices } from "./invoices.slice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { downloadQuickBooksCSV } from "@/services/quickbooks-csv.service";
import { fetchInvoices } from "./invoices.api";
import { toast } from "sonner";
import AppointmentQuickViewSheet from "@/app/pmc/appointments/components/AppointmentQuickViewSheet";
import { AppointmentJoin, AppointmentsRepository } from "@/repositories/appointments.repository";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { toInvoiceListDateFilters, type DateRange } from "@/lib/invoices/invoice-search";
import { useDebounce } from "@/hooks/useDebounce";
import { INVOICE_PAGE_SIZE_MAX } from "@shyne/shared/api";

const EXPORT_BATCH_SIZE = INVOICE_PAGE_SIZE_MAX;

const statusMap: Record<invoice_statuses, { color: string; icon: ReactElement; label: string }> = {
  [invoice_statuses.draft]: {
    color: "bg-gray-100 text-gray-700",
    icon: <FileText className="w-3 h-3 mr-1" />,
    label: "Draft",
  },
  [invoice_statuses.sent]: {
    color: "bg-blue-100 text-blue-700",
    icon: <Send className="w-3 h-3 mr-1" />,
    label: "Sent",
  },
  [invoice_statuses.paid]: {
    color: "bg-green-100 text-green-700",
    icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
    label: "Paid",
  },
  [invoice_statuses.void]: {
    color: "bg-red-100 text-red-700",
    icon: <Ban className="w-3 h-3 mr-1" />,
    label: "Void",
  },
};

function StatusBadge({ status }: { status: invoice_statuses }) {
  const { color, icon, label } = statusMap[status] ?? {
    color: "bg-gray-100 text-gray-700",
    icon: <FileText className="w-3 h-3 mr-1" />,
    label: status,
  };
  return (
    <span className={cn("inline-flex items-center px-2 py-1 rounded-sm text-[10px] uppercase w-full", color)}>
      {icon}
      {label}
    </span>
  );
}

export default function InvoicesPage() {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const [issueRange, setIssueRange] = useState<DateRange>({});
  const [dueRange, setDueRange] = useState<DateRange>({});
  const [statusFilter, setStatusFilter] = useState<ListedInvoiceStatus | "">("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState<string>("invoice_number");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceListJoin | null>(null);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [startInvoiceNumber, setStartInvoiceNumber] = useState<string>("");

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<ChangedInvoiceStatus | null>(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Appointment quick view state
  const [quickViewAppt, setQuickViewAppt] = useState<AppointmentJoin | null>(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  // Search hits the server; wait for typing to settle before querying.
  const debouncedSearch = useDebounce(search, 300);

  // Reset to page 1 when pageSize or filters change
  useEffect(() => {
    setPage(1);
  }, [pageSize, statusFilter, debouncedSearch, issueRange, dueRange]);

  // Reset to page 1 when sort changes
  useEffect(() => {
    setPage(1);
  }, [sortKey, sortOrder]);

  const filters: InvoiceListFilters = {
    status: statusFilter === "" ? undefined : statusFilter,
    search: debouncedSearch,
    ...toInvoiceListDateFilters(issueRange, dueRange),
  };

  const { invoices, loading, total, error } = useInvoices(page, pageSize, filters, sortKey, sortOrder);
  const { updateStatus } = useUpdateInvoiceStatus();
  const { bulkUpdateStatus } = useBulkUpdateInvoiceStatus();

  const handleVoidInvoiceClick = (invoice: InvoiceListJoin) => {
    setSelectedInvoice(invoice);
    setVoidDialogOpen(true);
  };

  const handleVoidInvoice = async () => {
    if (selectedInvoice) {
      await updateStatus(selectedInvoice.id, invoice_statuses.void);
      setVoidDialogOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleMarkAsPaidClick = (invoice: InvoiceListJoin) => {
    setSelectedInvoice(invoice);
    setPaidDialogOpen(true);
  };

  const handleMarkAsPaid = async () => {
    if (selectedInvoice) {
      await updateStatus(selectedInvoice.id, invoice_statuses.paid);
      setPaidDialogOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleSendInvoice = async (invoice: InvoiceListJoin) => {
    await updateStatus(invoice.id, invoice_statuses.sent);
  };

  // Bulk actions
  const handleBulkAction = (status: ChangedInvoiceStatus) => {
    setBulkAction(status);
    setBulkDialogOpen(true);
  };

  const handleBulkConfirm = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      await bulkUpdateStatus(Array.from(selectedIds), bulkAction);
      setSelectedIds(new Set());
      setBulkDialogOpen(false);
      setBulkAction(null);
      // Refresh the list
      dispatch(getInvoices({ page, pageSize, filters, sortKey, sortOrder }));
    } catch {
      // Error already handled by hook
    } finally {
      setBulkUpdating(false);
    }
  };

  // Appointment quick view
  const handleOpenQuickView = async (appointmentId: string) => {
    setQuickViewOpen(true);
    setQuickViewAppt(null);
    try {
      const data = await AppointmentsRepository.getById(appointmentId);
      setQuickViewAppt(data);
    } catch {
      toast.error("Failed to load appointment details");
      setQuickViewOpen(false);
    }
  };

  const breadcrumb = [
    {
      name: "Invoices",
      href: "#",
    },
  ];


  // Define columns for SimpleTable
  const columns: ColumnDef<InvoiceListJoin>[] = [
    {
      header: "Invoice #",
      accessor: (row) => (
        <Link to={`/invoices/${row.id}`} className="flex flex-col gap-1">
          <span className="text-primary text-sm font-semibold">#{row.invoice_number}</span>
          <span className="text-xs text-muted-foreground">ID: {row.id.slice(0, 8)}</span>
        </Link>
      ),
      sortable: true,
      sortKey: "invoice_number",
    },
    {
      header: "Billed For",
      accessor: (row) => {
        const subject = getInvoiceSubject(row);
        if (subject.presentation.referenceAction === "route") {
          return (
            <div className="text-sm">
              {subject.href ? (
                <Link to={subject.href} className="text-primary hover:underline">
                  {subject.presentation.billedForLabel}
                </Link>
              ) : (
                <span className="text-muted-foreground">{subject.presentation.billedForLabel}</span>
              )}
            </div>
          );
        }
        return (
          <div className="text-sm">
            {row.appointment_data?.id ? (
              <button
                className="text-primary hover:underline cursor-pointer"
                onClick={() => handleOpenQuickView(row.appointment_data.id)}
              >
                {subject.presentation.billedForLabel}
              </button>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            )}
          </div>
        );
      },
    },
    {
      header: "Property",
      accessor: (row) => {
        const subject = getInvoiceSubject(row);
        return (
          <div>
            <div className="text-sm">{subject.propertyName || <span className="text-muted-foreground">N/A</span>}</div>
            {subject.unitNumber && <div className="text-xs text-muted-foreground">Unit: {subject.unitNumber}</div>}
          </div>
        );
      },
    },
    {
      header: "Service",
      accessor: (row) => {
        const subject = getInvoiceSubject(row);
        return (
          <div>
            <div className="text-sm">{subject.serviceName || <span className="text-muted-foreground">N/A</span>}</div>
            {subject.serviceDetail && <div className="text-xs text-muted-foreground">{subject.serviceDetail}</div>}
          </div>
        );
      },
    },
    {
      header: "Amount",
      accessor: (row) => <span className="font-semibold">${row.amount.toFixed(2)}</span>,
      className: "text-right",
      cellClassName: "text-right",
    },
    {
      header: "Contractor Paid",
      accessor: (row) => {
        const profitability = getInvoiceProfitability(row);
        if (!profitability) return <span className="text-muted-foreground">—</span>;
        return <span>${profitability.contractorPaid.toFixed(2)}</span>;
      },
      className: "text-right",
      cellClassName: "text-right",
    },
    {
      header: "Gross Profit",
      accessor: (row) => {
        const profitability = getInvoiceProfitability(row);
        if (!profitability) return <span className="text-muted-foreground">—</span>;
        return (
          <span className={cn("font-semibold", profitability.grossProfit < 0 ? "text-red-600" : "text-green-600")}>
            ${profitability.grossProfit.toFixed(2)}
          </span>
        );
      },
      className: "text-right",
      cellClassName: "text-right",
    },
    {
      header: "Issue Date",
      accessor: (row) => (row.issue_date ? format(new Date(row.issue_date), "MMM dd, yyyy") : "N/A"),
      sortable: true,
      sortKey: "issue_date",
    },
    {
      header: "Due Date",
      accessor: (row) => (row.due_date ? format(new Date(row.due_date), "MMM dd, yyyy") : "N/A"),
      sortable: true,
      sortKey: "due_date",
    },
    {
      header: "Status",
      accessor: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Actions",
      accessor: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuItem>
              <Link to={`/invoices/${row.id}`} className="flex items-center w-full">
                <Eye className="w-4 h-4 mr-2" />
                View Invoice
              </Link>
            </DropdownMenuItem>
            {row.status === invoice_statuses.draft && (
              <DropdownMenuItem onClick={() => handleSendInvoice(row)}>
                <Send className="w-4 h-4 mr-2" />
                Send Invoice
              </DropdownMenuItem>
            )}

            {row.status !== invoice_statuses.paid && row.status !== invoice_statuses.void && (
              <DropdownMenuItem onClick={() => handleMarkAsPaidClick(row)}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Paid
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {row.status !== invoice_statuses.void && (
              <DropdownMenuItem variant="destructive" onClick={() => handleVoidInvoiceClick(row)}>
                <FileMinus2 className="w-4 h-4 mr-2" />
                Void Invoice
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      className: "whitespace-nowrap",
    },
  ];

  // Define filters for SimpleTable
  const filterItems: FilterItem[] = [
    {
      type: "search",
      title: "Search by property, unit, or invoice number...",
      value: search,
      onValueChange: setSearch,
      grid: 3,
    },
    {
      type: "dateRange",
      title: "Issue Date",
      value: issueRange,
      onValueChange: setIssueRange,
      grid: 3,
    },
    {
      type: "dateRange",
      title: "Due Date",
      value: dueRange,
      onValueChange: setDueRange,
      grid: 3,
    },
    {
      type: "select",
      title: "Invoice Status",
      value: statusFilter,
      onValueChange: (value) => setStatusFilter(value as ListedInvoiceStatus | ""),
      options: LISTED_INVOICE_STATUSES.map((s) => ({ label: statusMap[s].label, value: s })),
      grid: 3,
    },
  ];

  const handleSortChange = (key: string) => {
    if (sortKey === key) {
      // Toggle order if same key
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new key with ascending order
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const handleReset = () => {
    setSearch("");
    setStatusFilter("");
    setIssueRange({});
    setDueRange({});
    setSortKey("invoice_number");
    setSortOrder("asc");
  };

  const handleDownloadQuickBooksCSV = async (fromInvoiceNumber?: number) => {
    setDownloadingCSV(true);
    setDownloadModalOpen(false);
    try {
      const exportFilters: InvoiceListFilters = {
        invoiceNumberFrom: fromInvoiceNumber,
      };
      const invoicesToExport: InvoiceListJoin[] = [];
      for (let batchPage = 1; ; batchPage += 1) {
        const batch = await fetchInvoices(batchPage, EXPORT_BATCH_SIZE, exportFilters, "invoice_number", "asc");
        invoicesToExport.push(...batch.data);
        if (batch.data.length < EXPORT_BATCH_SIZE) break;
      }

      if (invoicesToExport.length === 0) {
        toast.info("No invoices found to export.");
        return;
      }

      // Download the CSV
      downloadQuickBooksCSV(invoicesToExport);
      toast.success(`Successfully exported ${invoicesToExport.length} invoice(s) to QuickBooks CSV.`);
    } catch (error: any) {
      console.error("Error downloading QuickBooks CSV:", error);
      toast.error(error?.message || "Failed to download QuickBooks CSV.");
    } finally {
      setDownloadingCSV(false);
      setStartInvoiceNumber(""); // Reset input
    }
  };

  const handleDownloadOK = () => {
    const invoiceNum = parseInt(startInvoiceNumber);
    if (isNaN(invoiceNum) || invoiceNum <= 0) {
      toast.error("Please enter a valid invoice number.");
      return;
    }
    handleDownloadQuickBooksCSV(invoiceNum);
  };

  const bulkActionLabel = bulkAction
    ? bulkAction === invoice_statuses.paid
      ? "Mark as Paid"
      : bulkAction === invoice_statuses.sent
        ? "Mark as Sent"
        : bulkAction === invoice_statuses.void
          ? "Void"
          : bulkAction
    : "";

  const downloadButton = (
    <>
      <Button
        variant="outline"
        size="lg"
        onClick={() => setDownloadModalOpen(true)}
        disabled={downloadingCSV || loading}
        className="uppercase rounded-lg"
      >
        <Download className="w-4 h-4 mr-2" />
        {downloadingCSV ? "Downloading..." : "Download CSV"}
      </Button>

      <Dialog open={downloadModalOpen} onOpenChange={setDownloadModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Export Invoices</DialogTitle>
            <DialogDescription>
              Enter the invoice number to start from. All invoices from this number to the latest will be exported, regardless of the table's current filters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="start-invoice" className="text-sm font-medium">
                Invoice Number (Start From)
              </label>
              <Input
                id="start-invoice"
                type="number"
                placeholder="Enter invoice number"
                value={startInvoiceNumber}
                onChange={(e) => setStartInvoiceNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleDownloadOK();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDownloadModalOpen(false);
                setStartInvoiceNumber("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleDownloadOK} disabled={!startInvoiceNumber || downloadingCSV}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return (
    <MainLayout rightSidebar={false} breadcrumb={breadcrumb}>
      <PageHeader title="Invoices" subtitle="View and manage invoices" additionalButtons={downloadButton} />
      <div className="space-y-6">
        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 bg-muted/50 border rounded-lg">
            <span className="text-sm font-medium">{selectedIds.size} invoice(s) selected</span>
            <div className="flex items-center gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={() => handleBulkAction(invoice_statuses.sent)}>
                <Send className="w-3 h-3 mr-1" />
                Mark as Sent
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction(invoice_statuses.paid)}>
                <CheckCircle className="w-3 h-3 mr-1" />
                Mark as Paid
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleBulkAction(invoice_statuses.void)}>
                <Ban className="w-3 h-3 mr-1" />
                Void
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            </div>
          </div>
        )}

        {error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-destructive mb-2">Error loading invoices</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : (
          <SimpleTable<InvoiceListJoin>
            columns={columns}
            data={invoices}
            loading={loading}
            totalCount={total}
            page={page}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            filters={filterItems}
            onReset={handleReset}
            container={false}
            selectable
            selectedRows={selectedIds}
            onSelectionChange={setSelectedIds}
            getRowId={(row) => row.id}
          />
        )}

        {/* Void Invoice Dialog */}
        <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Void Invoice</DialogTitle>
              <DialogDescription>
                Are you sure you want to void invoice #{selectedInvoice?.invoice_number}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoidDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleVoidInvoice}>
                Void Invoice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Paid Dialog */}
        <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark as Paid</DialogTitle>
              <DialogDescription>
                Are you sure you want to mark invoice #{selectedInvoice?.invoice_number} as paid?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaidDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleMarkAsPaid}>Mark as Paid</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Action Confirmation Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk {bulkActionLabel}</DialogTitle>
              <DialogDescription>
                Are you sure you want to {bulkActionLabel.toLowerCase()} {selectedIds.size} invoice(s)?
                {bulkAction === invoice_statuses.void && " This action cannot be undone."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkUpdating}>
                Cancel
              </Button>
              <Button
                variant={bulkAction === invoice_statuses.void ? "destructive" : "default"}
                onClick={handleBulkConfirm}
                disabled={bulkUpdating}
              >
                {bulkUpdating ? "Updating..." : `Confirm ${bulkActionLabel}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Appointment Quick View Sheet */}
      <AppointmentQuickViewSheet
        appointment={quickViewAppt}
        open={quickViewOpen}
        onClose={() => {
          setQuickViewOpen(false);
          setQuickViewAppt(null);
        }}
        userRole="vendor"
      />
    </MainLayout>
  );
}
